import axios from 'axios';
import { defineStore } from 'pinia';
import qs from 'qs';
import { User } from '../models/user';
import { authService } from '../services/auth.service';
import { orderService } from '../services/order.service';

export const useAuthStore = defineStore('auth', {
  state: () => ({

    /** @type {boolean} */
    isFetchingCurrentUser: false,

    /** @type {import('vue').Ref<User> } */
    currentUser: null,

    /** @type {import('vue').Ref<User> } */
    groupAdmin: null,

    /** @type {import('vue').Ref<User> } */
    learner: null,

    /** @type {import('vue').Ref<any> } */
    userFromV1: null,

    /** @type {boolean} */
    isPandargca: false,
  }),

  getters: {
    hasSelectedGroupAdmin: state => (state.groupAdmin !== null),
  },

  actions: {

    /** Logout user. */
    async logout() {
      await authService.logout();
      this.removeUser();
      this.setV1User(null);
    },

    /**
     * Sign in to the legacy session, then the API. A step the legacy login still asks for (staff MFA, or the SME
     * code with `smeMfa`) comes back first, without an API token. An SME passes the token from that code step
     * as `secret`.
     * @param {object} root0 Sign-in details.
     * @param {string} root0.username User name.
     * @param {string} root0.password Password.
     * @param {string|null} root0.loginMfaCode Staff code, for the staff code step.
     * @param {import('../models/user-secret').UserSecret|null} [root0.secret] Token from the SME code step.
     * @returns {Promise<object>} The legacy login response.
     */
    async login({
      username, password, loginMfaCode, secret: smeSecret = null,
    }) {
      const sessionLoginUrl = process.env.MIX_API_URL ? `${process.env.MIX_API_URL}/oldapi/loginControlInternal.php` : '/oldapi/loginControlInternal.php';
      const response = await axios.post(
        sessionLoginUrl,
        loginMfaCode ? qs.stringify({
          command: 'loginMfa',
          username,
          password,
          code: loginMfaCode,
        })
          : qs.stringify({
            command: 'login',
            username,
            password,
            ...(smeSecret ? { api_token: smeSecret.userSecret } : {}),
          })
        ,
      );

      if (!response.data?.result || response.data.mfa || response.data.sme_mfa) {
        return response.data;
      }

      const secret = smeSecret || await authService.login({
        username,
        password,
      });

      this.setV1User(secret);
      return response.data;
    },

    async removeUser() {
      this.currentUser = null;
    },

    async setV1User(user) {
      this.userFromV1 = user;
    },

    /**
     * Get profile and map to state
     * @returns {Promise<User>}
     */
    async getProfile() {
      try {
        this.isFetchingCurrentUser = true;
        const user = await authService.getProfile();
        this.currentUser = user;
        if (this.currentUser.isGroupAdmin) {
          this.setGroupAdmin(this.currentUser);
        }
      } catch (e) {
        throw new Error(e);
      } finally {
        this.isFetchingCurrentUser = false;
      }
      return this.currentUser;
    },

    /**
     * Set group admin
     * @param {User} newGroupAdmin Group admin
     */
    setGroupAdmin(newGroupAdmin) {
      this.groupAdmin = newGroupAdmin;
    },

    /** Remove group admin. */
    removeGroupAdmin() {
      this.groupAdmin = null;
    },

    /**
     * Set isPandargca flag.
     * @param {boolean} value Value.
     */
    setIsPandargca(value) {
      this.isPandargca = value;
    },

    /**
     * Check whether the impersonated manager is a pandargca user and set the flag.
     * @param {number} userId Impersonated manager id.
     */
    async fetchIsPandargca(userId) {
      if (!userId) {
        this.isPandargca = false;
        return;
      }

      try {
        const result = await orderService.checkIsPandargca(userId);
        this.isPandargca = result.isPandargca === true;
      } catch {
        this.isPandargca = false;
      }
    },

    /**
     * Set learner
     * @param {User} newLearner Learner
     */
    setLearner(newLearner) {
      this.learner = newLearner;
    },

    /** Remove learner. */
    removeLearner() {
      this.learner = null;
    },
  },
});
